/**
 * @file useDragToScroll.ts
 * @description Прокрутка полотна канбана «схватил и потянул» мышью
 * (замечание пользователя 2026-09-22): зажал левую кнопку на пустом месте
 * доски — тянешь колонки влево-вправо, как карту.
 *
 * Зачем хук, а не CSS: горизонтальный скролл колёсиком неочевиден, полоса
 * прокрутки внизу длинной доски не видна, а на 10+ колонках менеджер
 * теряет правый край. Тач и так тянет полотно нативно — хук работает только
 * для мыши, чтобы не спорить с браузерным скроллом на телефоне.
 *
 * Не конфликтует с перетаскиванием карточек (@dnd-kit): нажатие на карточку
 * (`role="button"`/`aria-roledescription="draggable"`), кнопку, ссылку или
 * поле ввода хук игнорирует — тянется только пустое место.
 *
 * Возвращает callback-ref, а не принимает `RefObject`: полотно доски
 * рендерится после загрузки и может перемонтироваться (компактный режим →
 * доска), а `useEffect` по ref-объекту увидел бы только первый монтаж.
 */

import { useCallback, useRef } from "react";

/** На чём НЕ начинаем тянуть полотно — у этих элементов своя реакция на мышь. */
const IGNORE_SELECTOR =
  'a, button, input, textarea, select, [role="button"], [aria-roledescription="draggable"], [data-no-drag-scroll]';

/** Минимальный сдвиг, после которого движение считаем прокруткой, а не кликом. */
const DRAG_THRESHOLD_PX = 4;

/** Навешивает обработчики на полотно; возвращает снятие. */
function attach(el: HTMLElement): () => void {
  let pointerId: number | null = null;
  let startX = 0;
  let startScrollLeft = 0;
  let dragging = false;

  const stop = (): void => {
    if (pointerId !== null && typeof el.releasePointerCapture === "function") {
      try {
        el.releasePointerCapture(pointerId);
      } catch {
        // Захват уже снят браузером (например, окно потеряло фокус).
      }
    }
    pointerId = null;
    dragging = false;
    el.classList.remove("cursor-grabbing", "select-none");
  };

  const onPointerDown = (e: PointerEvent): void => {
    // Только левая кнопка мыши: тач прокручивает нативно, правая — меню.
    if (e.button !== 0 || e.pointerType !== "mouse") return;
    if (el.scrollWidth <= el.clientWidth) return;
    const target = e.target as Element | null;
    if (target?.closest(IGNORE_SELECTOR)) return;
    pointerId = e.pointerId;
    startX = e.clientX;
    startScrollLeft = el.scrollLeft;
  };

  const onPointerMove = (e: PointerEvent): void => {
    if (pointerId === null || e.pointerId !== pointerId) return;
    const dx = e.clientX - startX;
    if (!dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
      dragging = true;
      el.classList.add("cursor-grabbing", "select-none");
      if (typeof el.setPointerCapture === "function") el.setPointerCapture(pointerId);
    }
    el.scrollLeft = startScrollLeft - dx;
    // Без этого браузер начинает выделять текст карточек по пути.
    e.preventDefault();
  };

  el.addEventListener("pointerdown", onPointerDown);
  el.addEventListener("pointermove", onPointerMove);
  el.addEventListener("pointerup", stop);
  el.addEventListener("pointercancel", stop);
  return () => {
    el.removeEventListener("pointerdown", onPointerDown);
    el.removeEventListener("pointermove", onPointerMove);
    el.removeEventListener("pointerup", stop);
    el.removeEventListener("pointercancel", stop);
    stop();
  };
}

export function useDragToScroll(): (el: HTMLElement | null) => void {
  const cleanup = useRef<(() => void) | null>(null);
  return useCallback((el: HTMLElement | null) => {
    cleanup.current?.();
    cleanup.current = el ? attach(el) : null;
  }, []);
}
